'use client';

import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { Check, EmailOutlined, ErrorOutline } from '@mui/icons-material';
import {
  QuestionnaireAdvancedModalShell,
  questionnaireAdvancedModalCancelButtonSx,
  questionnaireAdvancedModalPrimaryButtonSx,
} from '@/app/dashboard/questionnaire-actions/components/QuestionnaireAdvancedModalShell';
import type {
  BrochureDownloadFlowResult,
  BrochureDownloadFormValues,
  PublicBrochureDocument,
} from '../services/brochureDownloadService';
import {
  getBrochureFileName,
  resolveFeatureDocumentDownloadId,
  sendFeatureDocumentDownloadPdfEmail,
  submitFeatureDocumentDownload,
  triggerBrochureFileDownload,
} from '../services/brochureDownloadService';
import ResourceDocumentPdfPreview from './ResourceDocumentPdfPreview';
import {
  isBrochureFormValid,
  validateCompanyName,
  validateEmailAddress,
  validateFirstName,
  validateLastName,
  validatePhoneNumber,
  validateTitle,
} from './brochureDownloadValidation';
import { getResourceDocumentCopy } from '../utils/resourceDocumentCopy';

const BRAND_BLUE_DEEP = '#153C7F';
const BRAND_GREEN = '#63A821';
const LABEL_COLOR = '#1D2939';
const BORDER_COLOR = '#D0D5DD';

type BrochureStep =
  | 'form'
  | 'ready'
  | 'form-email-only'
  | 'email-confirm'
  | 'email-success'
  | 'email-error';

type FormFieldKey = keyof BrochureDownloadFormValues;

const FORM_FIELDS: FormFieldKey[] = [
  'firstName',
  'lastName',
  'email',
  'companyName',
  'title',
  'phoneNumber',
];

const REQUIRED_FORM_FIELDS: FormFieldKey[] = ['firstName', 'lastName', 'email'];

const OPTIONAL_FORM_FIELDS: FormFieldKey[] = ['companyName', 'title', 'phoneNumber'];

const FIELD_PLACEHOLDERS: Record<FormFieldKey, string> = {
  firstName: 'Enter first name',
  lastName: 'Enter last name',
  email: 'Enter email address',
  companyName: 'Enter company name',
  title: 'Enter title',
  phoneNumber: 'Enter phone number',
};

const FIELD_LABELS: Record<FormFieldKey, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email Address',
  companyName: 'Company Name',
  title: 'Title',
  phoneNumber: 'Phone Number',
};

const FIELD_AUTOCOMPLETE: Partial<Record<FormFieldKey, string>> = {
  firstName: 'given-name',
  lastName: 'family-name',
  email: 'email',
  companyName: 'organization',
  title: 'organization-title',
  phoneNumber: 'tel',
};

const uppercaseButtonSx = {
  textTransform: 'uppercase',
  fontWeight: 600,
  letterSpacing: '0.5px',
  fontFamily: 'Poppins, sans-serif',
} as const;

const outlineButtonSx = {
  ...questionnaireAdvancedModalCancelButtonSx,
  ...uppercaseButtonSx,
  color: BRAND_BLUE_DEEP,
  borderColor: BORDER_COLOR,
  '&:hover': {
    backgroundColor: '#F9FAFB',
    borderColor: '#98A2B3',
  },
} as const;

const FORM_MODAL_WIDTH = '580px';

const compactFormBodySx = {
  my: '-10px',
  py: 0,
} as const;

const compactFormActionsSx = {
  display: 'flex',
  gap: 1.5,
  width: '100%',
  justifyContent: 'flex-end',
  pt: '10px',
  mt: '-14px',
  mb: '-10px',
} as const;

const inputSx = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: '8px',
    backgroundColor: '#fff',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '0.875rem',
    '& fieldset': { borderColor: BORDER_COLOR, borderWidth: '1px', top: 0 },
    '& fieldset legend': { display: 'none' },
    '&:hover fieldset': { borderColor: '#98A2B3' },
    '&.Mui-focused fieldset': { borderColor: BRAND_GREEN, borderWidth: '1px' },
    '&.Mui-error fieldset': { borderColor: '#F04438' },
  },
  '& .MuiOutlinedInput-input': {
    py: 0,
    px: 1.5,
    height: '100%',
    boxSizing: 'border-box',
    color: LABEL_COLOR,
  },
  '& .MuiFormHelperText-root': {
    mx: 0,
    mt: 0.25,
    mb: 0,
    fontFamily: 'Poppins, sans-serif',
    fontSize: '0.72rem',
    minHeight: 'auto',
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: '#98A2B3',
    opacity: 1,
  },
};

function formatUSPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function sanitizePhoneInput(raw: string): string {
  return raw.replace(/[^\d()\s+-]/g, '');
}

function emptyFormValues(): BrochureDownloadFormValues {
  return {
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    title: '',
    phoneNumber: '',
  };
}

function emptyTouchedState(): Record<FormFieldKey, boolean> {
  return {
    firstName: false,
    lastName: false,
    email: false,
    companyName: false,
    title: false,
    phoneNumber: false,
  };
}

function emptyErrorState(): Record<FormFieldKey, string | null> {
  return {
    firstName: null,
    lastName: null,
    email: null,
    companyName: null,
    title: null,
    phoneNumber: null,
  };
}

function shouldValidateFormField(field: FormFieldKey, value: string): boolean {
  if (REQUIRED_FORM_FIELDS.includes(field)) {
    return true;
  }
  return value.trim().length > 0;
}

function isOptionalFormField(field: FormFieldKey): boolean {
  return field === 'companyName' || field === 'title' || field === 'phoneNumber';
}

function clearStaleOptionalFieldErrors(
  values: BrochureDownloadFormValues,
): Partial<Record<FormFieldKey, null>> {
  return {
    ...(!values.companyName.trim() ? { companyName: null } : {}),
    ...(!values.title.trim() ? { title: null } : {}),
    ...(!values.phoneNumber.trim() ? { phoneNumber: null } : {}),
  };
}

function getFormFieldDisplayError(
  field: FormFieldKey,
  value: string,
  touched: boolean,
  error: string | null,
): string | null {
  if (!touched || !error) {
    return null;
  }

  if (isOptionalFormField(field) && !value.trim()) {
    return null;
  }

  return error;
}

function validateField(field: FormFieldKey, value: string): string | null {
  if (!shouldValidateFormField(field, value)) {
    return null;
  }

  switch (field) {
    case 'firstName':
      return validateFirstName(value);
    case 'lastName':
      return validateLastName(value);
    case 'email':
      return validateEmailAddress(value);
    case 'companyName':
      return validateCompanyName(value);
    case 'title':
      return validateTitle(value);
    case 'phoneNumber':
      return validatePhoneNumber(value);
    default:
      return null;
  }
}

function buildLeadSubmitFormValues(values: BrochureDownloadFormValues): BrochureDownloadFormValues {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim(),
    companyName: values.companyName.trim() || '',
    title: values.title.trim() || '',
    phoneNumber: values.phoneNumber.trim() || '',
  };
}

function extractLeadSubmitErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  const err = error as {
    message?: string;
    details?: { message?: string; error?: string; error_message?: string } | string;
  };

  if (typeof err?.details === 'object' && err.details) {
    const detailMessage =
      err.details.message || err.details.error || err.details.error_message;
    if (typeof detailMessage === 'string' && detailMessage.trim()) {
      return detailMessage.trim();
    }
  }

  if (typeof err?.details === 'string' && err.details.trim()) {
    return err.details.trim();
  }

  if (typeof err?.message === 'string' && err.message.trim()) {
    return err.message.trim();
  }

  return 'Failed to submit your request. Please try again.';
}

function PrimaryButton({
  children,
  onClick,
  type,
  disabled,
  loading,
  form,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  form?: string;
}) {
  return (
    <Button
      type={type}
      form={form}
      onClick={onClick}
      variant="contained"
      disabled={disabled || loading}
      sx={{
        ...questionnaireAdvancedModalPrimaryButtonSx,
        ...uppercaseButtonSx,
        minHeight: 40,
        minWidth: 120,
      }}
    >
      {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : children}
    </Button>
  );
}

function SecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      onClick={onClick}
      variant="outlined"
      disabled={disabled}
      sx={{ ...outlineButtonSx, minHeight: 40 }}
    >
      {children}
    </Button>
  );
}

function FormField({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  disabled,
  autoComplete,
  inputMode,
  placeholder,
  required = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error: string | null;
  type?: string;
  disabled?: boolean;
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'tel';
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <Box sx={{ mb: '6px', '&:last-of-type': { mb: 0 } }}>
      <Typography
        component="label"
        htmlFor={id}
        sx={{
          display: 'block',
          mb: '2px',
          fontSize: '0.8rem',
          fontWeight: 600,
          color: LABEL_COLOR,
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        {label}
        {required ? (
          <Box component="span" sx={{ color: '#F04438', ml: 0.25 }}>
            *
          </Box>
        ) : null}
      </Typography>
      <TextField
        id={id}
        fullWidth
        hiddenLabel
        required={false}
        placeholder={placeholder}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        error={Boolean(error)}
        helperText={error || undefined}
        disabled={disabled}
        autoComplete={autoComplete}
        slotProps={{
          htmlInput: {
            ...(inputMode ? { inputMode } : {}),
            'aria-required': required,
            required: required || undefined,
          },
          formHelperText: {
            sx: {
              display: error ? 'block' : 'none',
            },
          },
        }}
        sx={inputSx}
      />
    </Box>
  );
}

function StepIcon({
  variant,
}: {
  variant: 'success' | 'error' | 'email';
}) {
  const bg =
    variant === 'error' ? '#F04438' : BRAND_GREEN;
  const icon =
    variant === 'error' ? (
      <ErrorOutline sx={{ fontSize: 30, color: '#fff' }} />
    ) : variant === 'email' ? (
      <EmailOutlined sx={{ fontSize: 30, color: '#fff' }} />
    ) : (
      <Check sx={{ fontSize: 30, color: '#fff' }} />
    );

  return (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        bgcolor: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 1.75,
        boxShadow: `0 8px 20px ${variant === 'error' ? 'rgba(240, 68, 56, 0.28)' : 'rgba(99, 168, 33, 0.28)'}`,
      }}
    >
      {icon}
    </Box>
  );
}

function CenteredBody({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        color: '#475467',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        fontFamily: 'Poppins, sans-serif',
        textAlign: 'center',
        maxWidth: 360,
        mx: 'auto',
      }}
    >
      {children}
    </Typography>
  );
}

function EmailDisplay({ email }: { email: string }) {
  return (
    <Typography
      sx={{
        mt: 1,
        fontWeight: 700,
        fontSize: '1rem',
        color: BRAND_BLUE_DEEP,
        fontFamily: 'Poppins, sans-serif',
        textAlign: 'center',
        wordBreak: 'break-word',
      }}
    >
      {email}
    </Typography>
  );
}

export interface BrochureDownloadModalProps {
  open: boolean;
  onClose: () => void;
  document: PublicBrochureDocument;
  featureMenuUrl: string;
  onSubmit: (payload: BrochureDownloadFormValues) => Promise<BrochureDownloadFlowResult>;
}

export default function BrochureDownloadModal({
  open,
  onClose,
  document: brochureDocument,
  featureMenuUrl,
  onSubmit,
}: BrochureDownloadModalProps) {
  const [step, setStep] = useState<BrochureStep>('form');
  const [values, setValues] = useState<BrochureDownloadFormValues>(emptyFormValues);
  const [errors, setErrors] = useState<Record<FormFieldKey, string | null>>(emptyErrorState);
  const [touched, setTouched] = useState<Record<FormFieldKey, boolean>>(emptyTouchedState);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [featureDocumentDownloadId, setFeatureDocumentDownloadId] = useState<number | null>(
    null,
  );
  const [leadSubmitEmail, setLeadSubmitEmail] = useState<string | null>(null);

  const emailEditMode = step === 'form-email-only';

  const resourceCopy = useMemo(
    () => getResourceDocumentCopy(brochureDocument.document_type),
    [brochureDocument.document_type],
  );

  const documentDisplayName = brochureDocument.document_name?.trim() || '';

  const resetForm = useCallback(() => {
    setStep('form');
    setValues(emptyFormValues());
    setErrors(emptyErrorState());
    setTouched(emptyTouchedState());
    setIsSubmittingLead(false);
    setIsDownloading(false);
    setIsSendingEmail(false);
    setSubmitError(null);
    setDownloadError(null);
    setDownloadUrl(null);
    setFeatureDocumentDownloadId(null);
    setLeadSubmitEmail(null);
  }, []);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  const runValidation = useCallback((field: FormFieldKey, value: string) => {
    if (OPTIONAL_FORM_FIELDS.includes(field) && !value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: null }));
      return;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));
  }, []);

  const handleFieldBlur = (field: FormFieldKey) => {
    if (OPTIONAL_FORM_FIELDS.includes(field) && !values[field].trim()) {
      setErrors((prev) => ({ ...prev, [field]: null }));
      setTouched((prev) => ({ ...prev, [field]: false }));
      return;
    }

    setTouched((prev) => ({ ...prev, [field]: true }));
    runValidation(field, values[field]);
  };

  const handleFieldChange = (field: FormFieldKey, rawValue: string) => {
    const nextValue =
      field === 'phoneNumber'
        ? formatUSPhone(sanitizePhoneInput(rawValue))
        : rawValue;
    setValues((prev) => ({ ...prev, [field]: nextValue }));

    if (isOptionalFormField(field) && !nextValue.trim()) {
      setErrors((prev) => ({ ...prev, [field]: null }));
      setTouched((prev) => ({ ...prev, [field]: false }));
    } else if (touched[field]) {
      runValidation(field, nextValue);
    }
  };

  const trimmedValues = useMemo(() => buildLeadSubmitFormValues(values), [values]);

  const isFormValid = isBrochureFormValid(
    values.firstName,
    values.lastName,
    values.email,
  );

  const isEmailValid = validateEmailAddress(values.email) === null;

  const handleLeadSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (emailEditMode) {
      const emailError = validateEmailAddress(values.email);
      setErrors((prev) => ({ ...prev, email: emailError }));
      setTouched((prev) => ({ ...prev, email: true }));
      if (emailError) return;
      setStep('email-confirm');
      return;
    }

    setErrors((prev) => ({
      ...prev,
      ...clearStaleOptionalFieldErrors(values),
    }));

    const nextErrors = emptyErrorState();
    nextErrors.companyName = null;
    nextErrors.title = null;
    nextErrors.phoneNumber = null;

    for (const field of FORM_FIELDS) {
      const isRequiredField = REQUIRED_FORM_FIELDS.includes(field);

      if (isRequiredField || values[field].trim()) {
        nextErrors[field] = validateField(field, values[field]);
      } else {
        nextErrors[field] = null;
      }
    }

    setErrors(nextErrors);
    setTouched((prev) => {
      const next = { ...prev };
      for (const field of FORM_FIELDS) {
        const isRequiredField = REQUIRED_FORM_FIELDS.includes(field);
        if (OPTIONAL_FORM_FIELDS.includes(field) && !values[field].trim()) {
          next[field] = false;
        } else if (isRequiredField || values[field].trim() || nextErrors[field]) {
          next[field] = true;
        }
      }
      return next;
    });

    if (FORM_FIELDS.some((field) => nextErrors[field])) {
      return;
    }

    setIsSubmittingLead(true);
    setSubmitError(null);

    try {
      const result = await onSubmit(buildLeadSubmitFormValues(values));
      setDownloadUrl(result.downloadUrl);
      setFeatureDocumentDownloadId(result.featureDocumentDownloadId);
      setLeadSubmitEmail(trimmedValues.email);
      setStep('ready');
    } catch (error: unknown) {
      setSubmitError(extractLeadSubmitErrorMessage(error));
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const handleDownload = async () => {
    setDownloadError(null);
    setIsDownloading(true);
    try {
      if (!downloadUrl) {
        throw new Error('PLEASE TRY AGAIN');
      }
      triggerBrochureFileDownload(downloadUrl, getBrochureFileName(brochureDocument));
    } catch {
      setDownloadError('PLEASE TRY AGAIN');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    const emailError = validateEmailAddress(values.email);
    if (emailError) {
      setErrors((prev) => ({ ...prev, email: emailError }));
      return;
    }

    setIsSendingEmail(true);

    try {
      let downloadId = featureDocumentDownloadId;
      const emailChanged =
        leadSubmitEmail !== null && trimmedValues.email !== leadSubmitEmail;

      if (emailChanged || !downloadId) {
        const postData = await submitFeatureDocumentDownload(
          buildLeadSubmitFormValues(values),
          brochureDocument,
          featureMenuUrl,
        );
        downloadId = resolveFeatureDocumentDownloadId(postData);
        if (downloadId) {
          setFeatureDocumentDownloadId(downloadId);
          setLeadSubmitEmail(trimmedValues.email);
        }
      }

      if (!downloadId) {
        setStep('email-error');
        return;
      }

      await sendFeatureDocumentDownloadPdfEmail(downloadId);
      setStep('email-success');
    } catch {
      setStep('email-error');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const isBusy = isSubmittingLead || isSendingEmail;
  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const shellMeta = useMemo(() => {
    switch (step) {
      case 'ready':
        return {
          title: 'Thank You!',
          subtitle: resourceCopy.readySubtitle,
        };
      case 'form-email-only':
        return {
          title: 'Change Email',
          subtitle: resourceCopy.changeEmailSubtitle,
        };
      case 'email-confirm':
        return {
          title: resourceCopy.deliverTitle,
          subtitle: undefined,
        };
      case 'email-success':
        return {
          title: resourceCopy.emailSuccessTitle,
          subtitle: undefined,
        };
      case 'email-error':
        return {
          title: resourceCopy.emailErrorTitle,
          subtitle: undefined,
        };
      default:
        return {
          title: resourceCopy.formTitle,
          subtitle: resourceCopy.formSubtitle(documentDisplayName),
        };
    }
  }, [step, resourceCopy, documentDisplayName]);

  const renderReadyActions = () => (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-end', sm: 'center' },
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: { xs: 1, sm: 1.5 },
        width: '100%',
        pt: 1.5,
        '& .MuiButton-root': {
          flex: '0 0 auto',
          width: 'auto',
          maxWidth: '100%',
        },
      }}
    >
      <PrimaryButton onClick={handleDownload} loading={isDownloading} disabled={isDownloading}>
        {isDownloading ? 'DOWNLOADING...' : resourceCopy.downloadLabel}
      </PrimaryButton>
      <SecondaryButton onClick={() => setStep('email-confirm')} disabled={isDownloading}>
        {resourceCopy.emailLabel}
      </SecondaryButton>
    </Box>
  );

  const renderBody = () => {
    if (step === 'form' || step === 'form-email-only') {
      const fieldsToShow: FormFieldKey[] = emailEditMode ? ['email'] : FORM_FIELDS;

      return (
        <Box component="form" id="brochure-download-form" onSubmit={handleLeadSubmit} noValidate sx={compactFormBodySx}>
          {fieldsToShow.map((field) => (
            <FormField
              key={field}
              id={`brochure-${field}`}
              label={FIELD_LABELS[field]}
              placeholder={FIELD_PLACEHOLDERS[field]}
              value={values[field]}
              onChange={(value) => handleFieldChange(field, value)}
              onBlur={() => handleFieldBlur(field)}
              error={getFormFieldDisplayError(field, values[field], touched[field], errors[field])}
              disabled={
                isSubmittingLead ||
                (emailEditMode && field !== 'email')
              }
              type={field === 'email' ? 'email' : field === 'phoneNumber' ? 'tel' : 'text'}
              autoComplete={FIELD_AUTOCOMPLETE[field]}
              inputMode={field === 'email' ? 'email' : field === 'phoneNumber' ? 'tel' : 'text'}
              required={emailEditMode ? field === 'email' : REQUIRED_FORM_FIELDS.includes(field)}
            />
          ))}

          {!emailEditMode && isSubmittingLead ? (
            <Typography
              role="status"
              sx={{
                mt: 0.75,
                color: BRAND_BLUE_DEEP,
                fontSize: '0.8rem',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                textAlign: 'center',
              }}
            >
              {resourceCopy.gettingResource}
            </Typography>
          ) : null}

          {submitError ? (
            <Typography role="alert" sx={{ mt: 0.75, color: '#B42318', fontSize: '0.8rem' }}>
              {submitError}
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (step === 'ready') {
      return (
        <Box
          sx={{
            flex: '1 1 0',
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <ResourceDocumentPdfPreview
            fillContainer
            downloadUrl={downloadUrl}
            fileName={getBrochureFileName(brochureDocument)}
            fallbackMessage={resourceCopy.previewUnavailable}
          />
          {downloadError ? (
            <Typography
              role="alert"
              sx={{
                mt: 1,
                flexShrink: 0,
                color: '#B42318',
                fontSize: '0.8rem',
                textAlign: 'center',
              }}
            >
              {downloadError}
            </Typography>
          ) : null}
          {renderReadyActions()}
        </Box>
      );
    }

    if (step === 'email-confirm') {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <StepIcon variant="email" />
          <CenteredBody>{resourceCopy.deliverBody}</CenteredBody>
          <EmailDisplay email={trimmedValues.email} />
          {isSendingEmail ? (
            <Typography
              role="status"
              sx={{ mt: 2, color: BRAND_BLUE_DEEP, fontSize: '0.85rem', fontWeight: 600 }}
            >
              SENDING EMAIL...
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (step === 'email-success') {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <StepIcon variant="success" />
          <CenteredBody>{resourceCopy.emailSentTo(trimmedValues.email)}</CenteredBody>
          {downloadError ? (
            <Typography role="alert" sx={{ mt: 1.5, color: '#B42318', fontSize: '0.8rem' }}>
              {downloadError}
            </Typography>
          ) : null}
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center' }}>
        <StepIcon variant="error" />
        <Typography
          role="alert"
          sx={{ mb: 1.5, color: '#B42318', fontSize: '0.85rem', fontFamily: 'Poppins, sans-serif' }}
        >
          {resourceCopy.emailFailedBody}
        </Typography>
        <CenteredBody>{resourceCopy.emailFailedFallback}</CenteredBody>
        {downloadError ? (
          <Typography role="alert" sx={{ mt: 1.5, color: '#B42318', fontSize: '0.8rem' }}>
            {downloadError}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const renderActions = () => {
    if (step === 'form') {
      return (
        <Box sx={compactFormActionsSx}>
          <SecondaryButton onClick={handleClose} disabled={isSubmittingLead}>
            CANCEL
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            form="brochure-download-form"
            disabled={!isFormValid}
            loading={isSubmittingLead}
          >
            {isSubmittingLead ? resourceCopy.gettingResource : resourceCopy.getResource}
          </PrimaryButton>
        </Box>
      );
    }

    if (step === 'form-email-only') {
      return (
        <Box sx={compactFormActionsSx}>
          <SecondaryButton onClick={() => setStep('email-confirm')} disabled={isSubmittingLead}>
            CANCEL
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            form="brochure-download-form"
            disabled={!isEmailValid}
          >
            USE THIS EMAIL
          </PrimaryButton>
        </Box>
      );
    }

    if (step === 'ready') {
      return null;
    }

    if (step === 'email-confirm') {
      return (
        <>
          <SecondaryButton
            onClick={() => setStep('form-email-only')}
            disabled={isSendingEmail}
          >
            CHANGE EMAIL
          </SecondaryButton>
          <PrimaryButton onClick={handleSendEmail} loading={isSendingEmail} disabled={isSendingEmail}>
            {isSendingEmail ? 'SENDING EMAIL...' : resourceCopy.sendToEmail}
          </PrimaryButton>
        </>
      );
    }

    if (step === 'email-success') {
      return (
        <>
          <SecondaryButton onClick={handleClose}>CLOSE</SecondaryButton>
          <PrimaryButton onClick={handleDownload} loading={isDownloading}>
            {isDownloading ? 'DOWNLOADING...' : resourceCopy.downloadLabel}
          </PrimaryButton>
        </>
      );
    }

    return (
      <>
        <SecondaryButton onClick={handleClose}>CLOSE</SecondaryButton>
        <SecondaryButton onClick={() => setStep('email-confirm')}>TRY AGAIN</SecondaryButton>
        <PrimaryButton onClick={handleDownload} loading={isDownloading}>
          {isDownloading ? 'DOWNLOADING...' : resourceCopy.downloadLabel}
        </PrimaryButton>
      </>
    );
  };

  const modalMaxWidth = step === 'ready' ? 'md' : 'sm';
  const isCompactFormStep = step === 'form' || step === 'form-email-only';
  const isPreviewStep = step === 'ready';

  return (
    <QuestionnaireAdvancedModalShell
      open={open}
      onClose={handleClose}
      title={shellMeta.title}
      subtitle={shellMeta.subtitle}
      width={isCompactFormStep ? FORM_MODAL_WIDTH : undefined}
      maxWidth={step === 'ready' ? 'md' : modalMaxWidth}
      height={isPreviewStep ? 'min(85vh, 720px)' : undefined}
      contentMinHeight={isPreviewStep ? '0' : undefined}
      actions={isPreviewStep ? undefined : renderActions()}
    >
      {renderBody()}
    </QuestionnaireAdvancedModalShell>
  );
}
